import { CommonModule } from '@angular/common';
import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { UserService } from '@app/auth/services/user.service';
import { Contact, ContactUpdateResult, ExtensionTypes } from '@app/contacts/models/contact';
import { ContactService } from '@app/contacts/services/contact.service';
import { BrandingField, defaultBrandingSettings } from '@app/core/models/branding.models';
import { AppFeature } from '@app/core/models/config.models';
import { AppConfigService } from '@app/core/services/app-config.service';
import { AppTranslateService } from '@app/core/services/app-translate.service';
import { BrandingService } from '@app/core/services/branding.service';
import { SnackbarService } from '@app/core/services/snack-bar.service';
import { DarkMode } from '@app/preferences/models/settings.models';
import { ContactComponent } from '@app/shared/components/contact/contact.component';
import { TransformImageComponent } from '@app/shared/components/resize-image/transform-image.component';
import { AvatarDirective } from '@app/shared/directives/avatar.directive';
import { BrandingColorDirective } from '@app/shared/directives/branding-color.directive';
import { PhoneNumberPipe } from '@app/shared/pipes/phone-number.pipe';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateModule } from '@ngx-translate/core';
import { base64ToFile } from 'ngx-image-cropper';
import { catchError, firstValueFrom, throwError } from 'rxjs';

import { ApiChangePassword } from '../../models/profile';
import { ChangePasswordComponent } from '../change-password/change-password.component';

@UntilDestroy()
@Component({
  standalone: true,
  imports: [
    MatIconModule,
    CommonModule,
    BrandingColorDirective,
    MatButtonModule,
    TranslateModule,
    PhoneNumberPipe,
    MatProgressSpinnerModule,
    FormsModule,
    MatRadioModule,
    MatSelectModule,
    ContactComponent,
    TransformImageComponent,
    ChangePasswordComponent,
    AvatarDirective,
  ],
  selector: 'app-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss'],
})
export class ProfileComponent implements OnInit {
  @ViewChild('file') fileInput!: ElementRef;
  @Input() profile: Contact | undefined;

  protected isChangingPassword = false;
  protected darkMode: DarkMode = 'light';
  protected edit = false;
  protected image = false;
  protected imageChangedEvent?: Event;
  protected editProfileLoading = false;
  protected avatarLoading = false;
  protected selectedHourFormat: string | null;
  protected localTimezone: string;
  protected DIDNumbers: string[] = [];
  protected readonly defaultAvatarsBaseUrl = 'https://www.gravatar.com';

  protected availableLanguages = AppTranslateService.availableLanguages;
  protected readonly AppFeature = AppFeature;
  protected readonly BrandingField = BrandingField;
  protected readonly ExtensionTypes = ExtensionTypes;
  protected defaultBrandingSettings = defaultBrandingSettings;

  constructor(
    private snackBar: SnackbarService,
    private contactService: ContactService,
    private userService: UserService,
    protected configService: AppConfigService,
    protected appTranslateService: AppTranslateService,
    protected brandingService: BrandingService
  ) {}

  ngOnInit(): void {
    this.localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    this.configService.data$.pipe(untilDestroyed(this)).subscribe((config) => {
      if (config && config.settings.preferences) {
        this.selectedHourFormat = config.settings.preferences.hourFormat;
        this.darkMode = config.settings.preferences.darkMode;
      }
    });
    this.DIDNumbers = this.configService.getDIDs();
  }

  protected fileChangeEvent(event: Event): void {
    this.imageChangedEvent = event;
    this.image = true;
  }

  protected save(resultImage: string) {
    this.avatarLoading = true;
    this.profile!.avatar = resultImage;
    this.saveProfilePhoto(base64ToFile(resultImage));
    this.image = false;
  }

  protected async removeAvatar() {
    this.avatarLoading = true;

    try {
      await this.contactService.removeAvatar(this.profile!.id);
      this.onSuccessProfileEdit();
      if (!this.profile?.firstName && !this.profile?.lastName) {
        this.profile!.avatar = 'assets/company.svg';
      } else {
        this.profile!.avatar = null;
      }
    } catch (error) {
      this.onFailedProfileEdit(error);
    }
  }

  protected async onUILanguageChanged(event: Event) {
    await this.appTranslateService.updateLanguage(String(event));
  }

  protected onPasswordChange($event: ApiChangePassword) {
    this.userService
      .changePassword($event)
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.snackBar.open(
          this.appTranslateService.instant('PROFILE.PASSWORD_UPDATED'),
          this.appTranslateService.instant('PROFILE.OK')
        );
        this.isChangingPassword = false;
      });
  }

  protected onPasswordChangeCanceled() {
    this.isChangingPassword = false;
  }

  protected getCoin(): string {
    return this.profile!.firstName?.charAt(0).toUpperCase() + this.profile!.lastName?.charAt(0).toUpperCase();
  }

  protected async onDataSubmitted(result: ContactUpdateResult) {
    this.editProfileLoading = true;

    try {
      await this.contactService.update(result.contact);
      this.edit = false;
      this.onSuccessProfileEdit();
    } catch (error) {
      this.onFailedProfileEdit(error);
      this.snackBar.open(
        this.appTranslateService.instant('PROFILE.PROFILE_UPDATE_FAILED', { error: error.error?.message }),
        this.appTranslateService.instant('PROFILE.OK')
      );
    }
  }

  private saveProfilePhoto(avatar: Blob) {
    const formData = new FormData();
    formData.append('avatar', avatar);
    this.contactService
      .uploadContactAvatar(this.profile!, formData)
      .pipe(
        untilDestroyed(this),
        catchError((error) => this.onFailedProfileEdit(error))
      )
      .subscribe(() => {
        this.onSuccessProfileEdit();
      });
  }

  private onFailedProfileEdit(error) {
    this.editProfileLoading = false;
    this.avatarLoading = false;
    return throwError(error);
  }

  private onSuccessProfileEdit() {
    this.editProfileLoading = false;
    this.avatarLoading = false;
    this.snackBar.open(
      this.appTranslateService.instant('PROFILE.PROFILE_UPDATED'),
      this.appTranslateService.instant('PROFILE.OK')
    );
  }

  protected onEditCanceled() {
    this.edit = false;
  }

  protected async onAppearenceChange() {
    await firstValueFrom(
      this.configService.updatePreferences({
        darkMode: this.darkMode,
      })
    );
  }

  protected async onHourFormatChange() {
    await firstValueFrom(
      this.configService.updatePreferences({
        hourFormat: this.selectedHourFormat!,
        timezone: this.localTimezone,
      })
    );
  }
}
